import React, { useEffect, useState } from 'react';
import { db, auth } from '../utils/firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { currencyService } from '../services/CurrencyService';

export const Success: React.FC = () => {
  const [sessionData, setSessionData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [userLanguage, setUserLanguage] = useState<string>(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return (urlParams.get('language')?.toUpperCase() === 'ES') ? 'es' : 'en';
  });

  useEffect(() => {
    const fetchSessionAndUpdateUser = async () => {
      try {
        // Extract session_id from URL parameters and remove any curly braces
        const urlParams = new URLSearchParams(window.location.search);
        const rawSessionId = urlParams.get('session_id');
        if (!rawSessionId) {
          throw new Error('Missing session_id parameter');
        }

        // Clean the session ID by removing any curly braces
        const sessionId = rawSessionId.replace(/[{}]/g, '');

        // Fetch session details from Stripe
        const response = await fetch(`https://api.stripe.com/v1/checkout/sessions/${sessionId}`, {
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_STRIPE_SECRET_KEY}`
          }
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to fetch session details: ${errorText}`);
        }
        const data = await response.json();
        setSessionData(data);

        // Get customer email from session data
        const customerEmail = data.customer_details?.email;
        if (!customerEmail) {
          throw new Error('Customer email not found in session data');
        }

        // Wait for Firebase auth state
        const user = await new Promise((resolve, reject) => {
          const unsubscribe = onAuthStateChanged(auth, (user) => {
            unsubscribe();
            if (user) {
              resolve(user);
            } else {
              reject(new Error('User not authenticated'));
            }
          });
        });

        // Update user's TopUpValue in Firebase using UID
        const userRef = doc(db, 'users', (user as any).uid);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const currentTopUpValue = userData.topUpValue || 0;
          const amountInEuros = data.amount_total / 100; // Convert cents to euros
          
          // Convert EUR to USD using our currency service
          const amountInDollars = await currencyService.toUSD(amountInEuros, 'EUR');
          
          await updateDoc(userRef, {
            topUpValue: currentTopUpValue + amountInDollars,
            lastUpdated: new Date().toISOString()
          });
        }

        setLoading(false);

        // Close window after a short delay to show success message
        setTimeout(() => {
          window.close();
        }, 8000);

      } catch (err) {
        console.error('Error processing payment success:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
        setLoading(false);
      }
    };

    window.scrollTo(0, 0);
    void fetchSessionAndUpdateUser();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Processing your payment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-red-50 to-white flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8 text-center">
          <div className="mb-8">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Payment Error</h2>
          <p className="text-gray-600 mb-8">{error}</p>
          <button
            onClick={() => window.close()}
            className="bg-red-500 hover:bg-red-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
          >
            Close Window
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center px-4">
      <div className="max-w-4xl w-full bg-white rounded-lg shadow-xl p-8">
        {/* Success Icon */}
        <div className="mb-8 text-center">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-green-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        </div>

        {/* Success Message */}
        {userLanguage === 'es' ? (
          <>
            <h2 className="text-3xl font-bold text-gray-800 mb-4 text-center">
              ¡Pago realizado con éxito!
            </h2>
            <p className="text-gray-600 mb-8 text-center">
              Gracias por tu compra. Tu cuenta ha sido recargada con éxito.
              <br />
              <span className="text-sm">Esta ventana se cerrará automáticamente...</span>
            </p>
          </>
        ) : (
          <>
            <h2 className="text-3xl font-bold text-gray-800 mb-4 text-center">
              Payment Successful!
            </h2>
            <p className="text-gray-600 mb-8 text-center">
              Thank you for your purchase. Your account has been topped up successfully.
              <br />
              <span className="text-sm">This window will close automatically...</span>
            </p>
          </>
        )}

        {/* Close Button */}
        <div className="text-center">
          <button
            onClick={() => window.close()}
            className="bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
          >
            Close Window
          </button>
        </div>
      </div>
    </div>
  );
};
